import { AuthContext } from '@urbanx/agx.auth.client';
import {
  type UploadedDocument,
  DocumentTypes,
  useStaffWithdrawContractOffer,
} from '@urbanx/agx.campaigns.client';
import { AgxFileUpload } from '@urbanx/agx-ui-components';
import {
  AgxAnimatedToggleLoaderOverlay,
  AgxButton,
  AgxDatePicker,
  AgxModal,
  AgxRadioGroup,
  AgxSpacer,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
  toDateOnlyString,
  useToast,
  YesNo,
} from '@urbanx/agx.ui-components2';
import { useSelectedCampaign } from 'hooks';
import { waitForTaskCollectionRefresh } from 'hooks/taskCollectionRefresh';
import { useContext, useState } from 'react';
import { isAxiosError } from 'axios';
import { deleteFile, getFileLink, uploadFile } from 'services';

interface Props {
  contractId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ContractWithdrawOfferModal = ({ contractId, isOpen, setIsOpen }: Props) => {
  const { campaign } = useSelectedCampaign();
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const [withdrawnDate, setWithdrawnDate] = useState<Date | null>(new Date());
  const [confirmationFiles, setConfirmationFiles] = useState<File[]>([]);
  const [depositHeldInTrust, setDepositHeldInTrust] = useState<boolean>();
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const { staffWithdrawContractOffer } = useStaffWithdrawContractOffer({
    onSuccess: () => {},
    onError: () => {},
  });

  const closeModal = () => {
    setWithdrawnDate(new Date());
    setConfirmationFiles([]);
    setDepositHeldInTrust(undefined);
    setIsOpen(false);
  };

  const confirmWithdrawal = async () => {
    const campaignId = campaign?.campaignId;
    const date = toDateOnlyString(withdrawnDate);
    const confirmationFile = confirmationFiles[0];
    if (!campaignId || !date || depositHeldInTrust === undefined || !confirmationFile) return;

    setIsWithdrawing(true);
    let cancelTaskCollectionRefresh: (() => void) | undefined;
    try {
      const authToken = await getAuthToken();
      if (!authToken) return;

      const renamedFile = new File(
        [confirmationFile],
        `${contractId}/${DocumentTypes.WithdrawalConfirmation}/${confirmationFile.name}`,
        confirmationFile
      );
      const formData = new FormData();
      formData.append('File', renamedFile);
      let buyerWithdrawalConfirmation: UploadedDocument | undefined;
      try {
        buyerWithdrawalConfirmation = await uploadFile(
          authToken,
          formData,
          campaignId,
          DocumentTypes.WithdrawalConfirmation,
          null,
          null,
          undefined,
          contractId
        );
      } catch (error) {
        pushToast({
          type: 'error',
          message:
            error instanceof Error && !isAxiosError(error)
              ? error.message
              : 'The buyer withdrawal confirmation could not be uploaded. Check the file and try again.',
        });
        return;
      }

      if (!buyerWithdrawalConfirmation) {
        pushToast({
          type: 'error',
          message:
            'The buyer withdrawal confirmation could not be uploaded. Check the file and try again.',
        });
        return;
      }

      const taskCollectionRefresh = waitForTaskCollectionRefresh(campaignId, contractId);
      cancelTaskCollectionRefresh = taskCollectionRefresh.cancel;
      await staffWithdrawContractOffer({
        campaignId,
        contractId,
        body: { withdrawnDate: date, depositHeldInTrust, buyerWithdrawalConfirmation },
      });

      if ((await taskCollectionRefresh.wait()) === 'timed-out') {
        pushToast({
          type: 'error',
          message:
            'The offer was withdrawn, but the task refresh could not be confirmed. Refresh the page to check its latest status.',
        });
      }

      closeModal();
    } finally {
      cancelTaskCollectionRefresh?.();
      setIsWithdrawing(false);
    }
  };

  return (
    <AgxModal
      title="Withdraw Offer"
      titleIcon={
        <>
          {isWithdrawing && (
            <AgxAnimatedToggleLoaderOverlay background="transparent" label="Withdrawing offer..." />
          )}
          <Icon_FeaturedIcon
            icon={<Icon_AlertCircleOutline type="Danger" />}
            color="var(--text-error)"
            backgroundColor="var(--bg-error-light)"
          />
        </>
      }
      type="single"
      variant="withDivider"
      actionButtonContainerStyle="withoutDivider"
      actionButtons={
        <>
          <AgxButton
            id="btnCancelWithdrawOffer"
            text="Cancel"
            variant="hollow"
            size="large"
            onClick={closeModal}
            stretch
          />
          <AgxButton
            id="btnConfirmWithdrawOffer"
            text="Confirm"
            variant="primary"
            danger
            size="large"
            onClick={() => void confirmWithdrawal()}
            stretch
            disabled={
              !withdrawnDate ||
              confirmationFiles.length === 0 ||
              depositHeldInTrust === undefined ||
              isWithdrawing
            }
          />
        </>
      }
      content={
        <AgxSpacer
          direction="vertical"
          gap="md"
          fill
          padding={{ top: 'md', bottom: 'xl', left: 'xl', right: 'xl' }}
        >
          <AgxDatePicker
            id="withdrawnDate"
            label="Withdrawn date"
            optionalText=""
            stretch
            required
            value={withdrawnDate}
            onChange={setWithdrawnDate}
          />
          <AgxFileUpload
            title="Buyer Withdrawal Confirmation"
            id="fileBuyerWithdrawalConfirmation"
            documentType={DocumentTypes.WithdrawalConfirmation}
            buttonText="Choose file"
            fileData={confirmationFiles}
            onValueChanged={({ value }: { value: File[] }) => setConfirmationFiles(value)}
            getAuthToken={getAuthToken}
            campaignId={campaign?.campaignId ?? ''}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
          />
          <AgxRadioGroup
            id="depositHeldInTrust"
            label="Is a deposit held in trust?"
            layout="vertical"
            options={[
              { label: 'Yes', value: YesNo.Yes },
              { label: 'No', value: YesNo.No },
            ]}
            value={
              depositHeldInTrust === undefined
                ? undefined
                : depositHeldInTrust
                  ? YesNo.Yes
                  : YesNo.No
            }
            onChange={(value) => setDepositHeldInTrust(value === YesNo.Yes)}
            required
          />
        </AgxSpacer>
      }
      isOpen={isOpen}
      setIsDialogOpen={(nextIsOpen) => !nextIsOpen && closeModal()}
    />
  );
};

export default ContractWithdrawOfferModal;
